import { AuthHeaderMenu } from '@/components/auth-header-menu';
import { LanguageSelector } from '@/components/language-selector';
import { MobileMenu } from '@/components/mobile-menu';
import { Text } from '@/components/text';
import { Pressable, View } from '@/components/ui/tw-wrappers';
import { HEADER_HEIGHT } from '@/constants/layout';
import { MENU_ITEMS } from '@/constants/navigation';
import { Colors } from '@/constants/theme';
import { openEmailContact } from '@/utils/mail';
import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { usePathname, useRouter } from 'expo-router';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, StyleSheet } from 'react-native';
import Animated, { SharedValue, useAnimatedStyle } from 'react-native-reanimated';

interface AnimatedHeaderProps {
  translateY: SharedValue<number>;
}

export const MARGIN_HEADER = 15;
const HIDDEN_ROUTES = ['/admin', '/forgot-password'];

export function AnimatedHeader({ translateY }: AnimatedHeaderProps) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const router = useRouter();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isVisible = !HIDDEN_ROUTES.includes(pathname);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateY: translateY.value }],
      zIndex: 100,
      opacity: isVisible ? 1 : 0,
    };
  });

  return (
    <Animated.View
      style={[styles.container, { height: HEADER_HEIGHT }, animatedStyle]}
      pointerEvents={isVisible ? 'auto' : 'none'}>
      <View style={styles.centerContainer}>
        <View style={styles.content} className='justify-between lg:justify-center'>
          <View className='lg:absolute lg:left-6'>
            <Pressable
              onPress={() => router.push('/')}
              style={({ pressed }) => [styles.logoContainer, pressed && { opacity: 0.5 }]}>
              <Image
                source={require('@/assets/images/icon.png')}
                style={styles.logo}
                contentFit='contain'
                alt={t('common.iconAlt', 'PangeX Icon')}
                priority='high'
                cachePolicy='memory-disk'
                transition={0}
              />

              <View className='hidden lg:flex'>
                <Text type='title' style={styles.headerTitleDesktop}>
                  {t('header.title')}
                </Text>
              </View>
              <View className='flex lg:hidden'>
                <Text type='defaultSemiBold'>{t('header.title')}</Text>
              </View>
            </Pressable>
          </View>

          <View style={styles.desktopMenu} className='hidden lg:flex'>
            {MENU_ITEMS.map(item => {
              const isActive = pathname === item.path || (pathname === '/compare' && item.path === '/');
              return (
                <Pressable
                  key={item.key}
                  onPress={() => {
                    if (item.path.startsWith('http')) {
                      Linking.openURL(item.path);
                    } else if (item.path.startsWith('mailto:')) {
                      openEmailContact();
                    } else {
                      router.push(item.path as any);
                    }
                  }}>
                  <Text type={isActive ? 'defaultBold' : 'default'} style={styles.menuItemText}>
                    {t(item.key)}
                  </Text>
                </Pressable>
              );
            })}
          </View>

          <View style={styles.rightActions} className='lg:absolute lg:right-[38px]'>
            <View className='hidden lg:flex flex-row items-center gap-5'>
              <LanguageSelector />
              <AuthHeaderMenu />
            </View>
            <View className='flex lg:hidden'>
              <Pressable
                onPress={() => setIsMobileMenuOpen(true)}
                style={({ pressed }) => [styles.menuButton, pressed && styles.menuButtonPressed]}>
                <Ionicons name='menu' size={28} color='#000' />
              </Pressable>
            </View>
          </View>
        </View>
      </View>
      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} pathname={pathname} />
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: MARGIN_HEADER,
    left: MARGIN_HEADER,
    right: MARGIN_HEADER,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    shadowColor: Colors.light.shadow,
    shadowOffset: { width: 4, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 2,
    borderRadius: 38,
    height: HEADER_HEIGHT,
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    height: HEADER_HEIGHT,
    paddingHorizontal: 24,
  },
  centerContainer: {
    width: '100%',
    alignSelf: 'center',
    flex: 1,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 15,
  },
  logo: {
    width: 40,
    height: 40,
  },
  headerTitleDesktop: {
    fontSize: 18,
  },
  desktopMenu: {
    flexDirection: 'row',
    gap: 50,
  },
  menuItemText: {
    fontSize: 15,
  },
  rightActions: {
    flexDirection: 'row',
    gap: 20,
    alignItems: 'center',
  },
  menuButton: {
    padding: 4,
    marginLeft: 4,
  },
  menuButtonPressed: {
    opacity: 0.7,
  },
});
